import { AppConfirmModal, Details } from 'components/organisms';
import { appPaths } from 'consts/paths/paths';
import { commonTexts, settingsTexts } from 'consts/text';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { EmptyDisplay } from 'components/molecules';
import { isAxiosError } from 'axios';
import { apiErrorCodes } from 'consts/api/apiErrorCodes';
import { AppButton, AppFlex } from 'components/atoms';
import { IconEdit, IconPlugConnectedX } from '@tabler/icons-react';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import styled, { css, useTheme } from 'styled-components';
import { useState } from 'react';
import { colors } from 'theme/styledComponents/colors';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { useQueryClient } from '@tanstack/react-query';
import { useSettingsAbilities } from 'abilities';

const StyledButtonsContainer = styled(AppFlex)`
    ${({ theme }) => css`
        width: 100%;

        @media ${theme.sizes.mediaQueries.mobileMax} {
            flex-direction: column-reverse;
        }
    `}
`;

export const PayUCredentialsDetailsPage = () => {
    const [isRemovePayUCredentialsConfirmModalOpen, setIsRemovePayUCredentialsConfirmModalOpen] =
        useState(false);

    const queryClient = useQueryClient();

    const { statefulNavigate } = useNavigateWithState();
    const { displaySuccess, displayError } = useSimpleNotification();

    const isMobile = useIsMobile();
    const theme = useTheme();

    const { id: currentInstitutionId } = useCurrentInstitution();

    const { settingsAbilities } = useSettingsAbilities();

    const {
        data: payUCredentialsData,
        error: payUCredentialsError,
        isFetching: isPayUCredentialsFetching,
        isError: isPayUCredentialsError,
    } = useAppQuery('PAY_U_CREDENTIALS', [], StaffHTTPService.settings.getPayUCredentials, {
        retry: false,
    });

    const { checkIfCanManageSettlementSettings } = settingsAbilities;
    const canManageSettlementSettings = checkIfCanManageSettlementSettings();

    const closeRemovePayUCredentialsConfirmModal = () =>
        setIsRemovePayUCredentialsConfirmModalOpen(false);

    const handleRemovePayUCredentialsSuccess = async () => {
        queryClient.removeQueries([currentInstitutionId, 'PAY_U_CREDENTIALS'], {
            exact: true,
        });
        closeRemovePayUCredentialsConfirmModal();
        displaySuccess(
            settingsTexts.payUCredentials.removeConfirmModal.notifications.successfullyRemoved,
        );
    };

    const handleRemovePayUCredentialsError = () => {
        closeRemovePayUCredentialsConfirmModal();
        displayError(commonTexts.errorMessages.unknown);
    };

    const { mutate: removePayUCredentials, isLoading: isRemovePayUCredentialsLoading } =
        useAppMutation(StaffHTTPService.settings.removePayUCredentials, {
            key: ['REMOVE_PAYU_CREDENTIALS'],
            onSuccess: handleRemovePayUCredentialsSuccess,
            onError: handleRemovePayUCredentialsError,
        });

    const handleEditPayUCredentialsButtonClick = () =>
        statefulNavigate(appPaths.app.settings['payu-credentials'].edit);

    const handleRemovePayUCredentialsButtonClick = () =>
        setIsRemovePayUCredentialsConfirmModalOpen(true);

    const isPayUCredentialsNotFoundErrorCode =
        isAxiosError(payUCredentialsError) &&
        payUCredentialsError.response?.data.code === apiErrorCodes.PAYU_CREDENTIALS_NOT_FOUND;

    const renderContent = () => {
        if (isPayUCredentialsNotFoundErrorCode) {
            return (
                <EmptyDisplay
                    emptyInfo={settingsTexts.payUCredentials.details.noPayUCredentialsMessage}
                />
            );
        }

        return (
            <>
                <Details.Item title={settingsTexts.payUCredentials.details.labels.saleIdPoint}>
                    {payUCredentialsData?.pointOfSaleId}
                </Details.Item>
                <Details.Item title={settingsTexts.payUCredentials.details.labels.secondKey}>
                    {payUCredentialsData?.secondKey}
                </Details.Item>
                <Details.Item title={settingsTexts.payUCredentials.details.labels.clientId}>
                    {payUCredentialsData?.clientId}
                </Details.Item>
                <Details.Item title={settingsTexts.payUCredentials.details.labels.clientSecret}>
                    {payUCredentialsData?.clientSecret}
                </Details.Item>
            </>
        );
    };

    return (
        <>
            <AppConfirmModal
                open={isRemovePayUCredentialsConfirmModalOpen}
                title={settingsTexts.payUCredentials.removeConfirmModal.title}
                text={settingsTexts.payUCredentials.removeConfirmModal.description}
                icon={<IconPlugConnectedX style={{ color: colors.red.red6 }} />}
                width={400}
                confirmActionLoading={isRemovePayUCredentialsLoading}
                okButtonProps={{ title: commonTexts.actionLabels.confirm, danger: true }}
                cancelButtonProps={{ title: commonTexts.actionLabels.reject }}
                onOk={() => removePayUCredentials()}
                onCancel={() => setIsRemovePayUCredentialsConfirmModalOpen(false)}
            />
            <AppFlex vertical align="center" gap={theme.sizes.marginLarge}>
                <Details
                    title={settingsTexts.payUCredentials.details.title}
                    editButtonText={
                        isPayUCredentialsNotFoundErrorCode
                            ? settingsTexts.payUCredentials.details.noPayUCredentialsButtonText
                            : commonTexts.actionLabels.edit
                    }
                    isLoading={isPayUCredentialsFetching}
                    isVisibleGoBackButton={false}
                    isError={isPayUCredentialsNotFoundErrorCode ? false : isPayUCredentialsError}
                >
                    {renderContent()}
                </Details>
                {canManageSettlementSettings && !isPayUCredentialsFetching && (
                    <StyledButtonsContainer gap="large" justify="center">
                        {!isPayUCredentialsNotFoundErrorCode && (
                            <AppButton
                                type="primary"
                                danger
                                icon={<IconPlugConnectedX size={20} />}
                                margin={0}
                                fullWidth={isMobile}
                                onClick={handleRemovePayUCredentialsButtonClick}
                            >
                                {
                                    settingsTexts.payUCredentials.details
                                        .deletePayUCredentialsButtonText
                                }
                            </AppButton>
                        )}
                        <AppButton
                            type="primary"
                            icon={<IconEdit size={20} />}
                            margin={0}
                            fullWidth={isMobile}
                            onClick={handleEditPayUCredentialsButtonClick}
                        >
                            {isPayUCredentialsNotFoundErrorCode
                                ? settingsTexts.payUCredentials.details.addPayUCredentialsButtonText
                                : settingsTexts.payUCredentials.details
                                      .editPayUCredentialsButtonText}
                        </AppButton>
                    </StyledButtonsContainer>
                )}
            </AppFlex>
        </>
    );
};
